import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check } from "lucide-react";
import { TID } from "@/constants/testIds";
import { track } from "@/lib/analytics";
import { submitContact, submitBrochureLead } from "@/lib/api";

/**
 * Quick lead capture form used inside the auto pop-ups and the sticky
 * "Download Brochure" modal. Same shape as the Book-Site-Visit Step 1 plus a
 * "looking_for" dropdown and a compact LinkedIn/company field.
 *
 * Props:
 *  - source: analytics + backend source tag (e.g. "scroll_popup", "brochure_download")
 *  - variant: "brochure" | "contact"  — decides the API endpoint + confirmation copy
 *  - onSubmitted: optional callback fired after success
 *  - ctaLabel: submit button text
 */
export default function QuickLeadForm({
  source = "quick_lead",
  variant = "contact",
  onSubmitted,
  ctaLabel,
}) {
  const [values, setValues] = useState({
    name: "",
    mobile: "",
    email: "",
    city: "",
    looking_for: "",
    profile: "",
    consent: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    track("form_open", { form: variant === "brochure" ? "brochure" : "quick_lead", source });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChange = (k) => (e) => {
    const val = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setValues((v) => ({ ...v, [k]: val }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!values.consent) {
      toast.error("Please tick the consent box before submitting.");
      return;
    }
    setSubmitting(true);
    try {
      const fn = variant === "brochure" ? submitBrochureLead : submitContact;
      await fn({ ...values, source });
      setDone(true);
      track("form_submit", { form: variant === "brochure" ? "brochure" : "quick_lead", source, email: values.email, mobile: values.mobile, name: values.name });
      toast.success(
        variant === "brochure"
          ? "Sent. Check your inbox for the brochure."
          : "Received. We'll be in touch soon.",
      );
      onSubmitted?.();
    } catch (err) {
      track("form_error", { form: variant === "brochure" ? "brochure" : "quick_lead", source });
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="border border-forest/20 rounded-lg p-6 bg-white text-center space-y-3">
        <div className="w-11 h-11 rounded-full bg-lime mx-auto grid place-items-center">
          <Check className="text-void" size={20} />
        </div>
        <div className="font-display text-xl text-forest">
          {variant === "brochure" ? "Brochure on its way." : "Thank you."}
        </div>
        <div className="text-sm text-forest/70 max-w-md mx-auto leading-relaxed">
          {variant === "brochure"
            ? "Check your inbox — it's attached to the email. If it doesn't arrive in 2 minutes, ping us on WhatsApp."
            : "A member of our team will reach out on the number you shared."}
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-3.5">
      <div className="grid sm:grid-cols-2 gap-3.5">
        <input
          data-testid={TID.quickLeadName}
          required
          placeholder="Full name *"
          value={values.name}
          onChange={onChange("name")}
          className={inputCls}
        />
        <input
          data-testid={TID.quickLeadMobile}
          required
          placeholder="Mobile *"
          inputMode="tel"
          value={values.mobile}
          onChange={onChange("mobile")}
          className={inputCls}
        />
        <input
          data-testid={TID.quickLeadEmail}
          required
          placeholder="Email *"
          type="email"
          value={values.email}
          onChange={onChange("email")}
          className={inputCls}
        />
        <input
          data-testid={TID.quickLeadCity}
          required
          placeholder="City *"
          value={values.city}
          onChange={onChange("city")}
          className={inputCls}
        />
      </div>

      <select
        data-testid={TID.quickLeadLookingFor}
        required
        value={values.looking_for}
        onChange={onChange("looking_for")}
        className={`${inputCls} appearance-none cursor-pointer`}
      >
        <option value="" disabled>
          What are you looking for? *
        </option>
        <option>Plot for building my home</option>
        <option>Investment / land SIP outlook</option>
        <option>Second home / weekend home</option>
        <option>Just curious — exploring the corridor</option>
      </select>

      <input
        data-testid={TID.quickLeadProfile}
        required
        placeholder="LinkedIn URL, or Company & Designation *"
        value={values.profile}
        onChange={onChange("profile")}
        className={inputCls}
      />

      <label className="flex items-start gap-3 pt-1">
        <input
          data-testid={TID.quickLeadConsent}
          type="checkbox"
          required
          checked={values.consent}
          onChange={onChange("consent")}
          className="mt-1 w-4 h-4 accent-[color:var(--phc-forest)]"
        />
        <span className="text-[11px] text-forest/80 leading-relaxed">
          I consent to being contacted via WhatsApp, Call, and SMS regarding
          Pali Hill City Chapter III. (Required per TRAI regulations.)
        </span>
      </label>

      <button
        data-testid={TID.quickLeadSubmit}
        disabled={submitting}
        className="btn-primary w-full sm:w-auto mt-1"
      >
        {submitting ? "Sending…" : ctaLabel || (variant === "brochure" ? "Email me the brochure" : "Send")}
      </button>
    </form>
  );
}

const inputCls =
  "w-full border-0 border-b border-forest/25 bg-transparent py-2.5 px-0 text-forest placeholder:text-forest/40 focus:outline-none focus:border-forest transition-colors text-[14px] font-body";
